import { Icons } from '@eppendorf/vnls-css/dist';
import { DeviceConnectionStatus } from '@eppendorf/vnls-device-service-utils';
import i18next from 'i18next';

import { getCountryName, isValidISOCountry } from '$shared/utils/country.utils';

export function getConnectionStatusText(
  connectionStatus: DeviceConnectionStatus | undefined,
): string {
  switch (connectionStatus) {
    case 'pending':
    case 'readyToPair':
      return i18next.t('translation:deviceConnectionStatus.pending');
    case 'paired':
      return i18next.t('translation:deviceConnectionStatus.paired');
    case 'waitingForMetadata':
      return i18next.t('translation:deviceConnectionStatus.waitingForMetadata');
    default:
      return '';
  }
}

// Temporary solution, until this comes from the browser lib
const deviceIcons: Set<Icons> = new Set([
  'centrifuge-multi-purpose',
  'centrifuge-large',
  'centrifuge',
  'electronic-pipette',
  'ep-motion',
  'freezer',
  'generic',
  'incubator',
  'incubator-c170i',
  'incubator-galaxy-170',
  'mixing-heating',
  'pipette',
  'scivario-twin',
  'sensor-multi-mobile-attachable',
  'shaker-open-air',
  'shaker-s41i',
  'shaker-s44i',
  'shaker',
  'small-bioreactor',
  'spectrometer',
  'thermocycler',
  'thermomixer',
  'sense-monitor',
  'sense-sensor',
  'sense-gateway',
  'vn-box',
]);

/**
 * Model-specific icon overrides.
 *
 * By default, the device icon is resolved from the device *type* (see `deviceIcons` above),
 * which means every device sharing a type also shares the same icon. Add an entry here,
 * keyed by the exact device `model` string, to force a specific icon for that model,
 * regardless of its resolved device type. This takes precedence over the type-based lookup.
 *
 * Example:
 * const deviceIconOverridesByModel = new Map<string, Icons>([
 *   ['CryoCube F740', 'freezer'],
 * ]);
 */
const deviceIconOverridesByModel = new Map<string, Icons>([
  ['SpinPro 8 R', 'centrifuge-floor-standing'],
  ['Innova S44i', 'shaker-s44i'],
  ['CellXpert CS220', 'shaker-cs220'],
  ['CryoCube 6', 'freezer-600b'],
]);

export function searchDeviceIcon(
  deviceType: string | undefined,
  isUnmasked?: boolean,
  model?: string,
): Icons {
  if (model && deviceIconOverridesByModel.has(model)) {
    return deviceIconOverridesByModel.get(model) as Icons;
  }

  if (!deviceType) {
    return 'generic';
  }

  const unmaskedIcon = `${deviceType}_unmasked` as Icons;
  const maskedIcon = deviceType as Icons;
  const senseIcon = `sense-${deviceType}` as Icons;

  if (deviceIcons.has(maskedIcon)) {
    return isUnmasked ? unmaskedIcon : maskedIcon;
  }

  if (deviceIcons.has(senseIcon)) {
    return senseIcon;
  }

  return 'generic';
}

export const resolveDeviceMetaDataValue = (value: string | undefined) =>
  value && value.length ? value : undefined;

export const resolveDeviceMetaCountryValue = (
  value: string | undefined,
  notFoundMessage: string,
) => {
  if (value?.length && isValidISOCountry(value)) {
    return getCountryName(value)?.label;
  }

  if (value?.length) {
    return notFoundMessage;
  }

  return undefined;
};
